<template>
  <v-autocomplete
    v-bind="$attrs"
    :items="items"
    :menu-props="{ closeOnClick: true }"
    :dense="dense"
    :outlined="outlined"
    hide-selected
    ref="vuecomp"
    :search-input.sync="search"
    :value="value"
    :multiple="multiple"
    :item-value="idField"
    :item-text="nameField"
    :loading="isLoading"
    :filter="filter"
    class="pt-0"
    @input="handleInputChange"
  >
    <template v-slot:item="{ item }">
      <slot name="item" :item="item">
        {{ item[nameField] }}
      </slot>
    </template>
    <template v-slot:selection="{ attrs, selected, item }">
      <slot name="selection" :attrs="attrs" :selected="selected" :item="item">
        <v-chip
          small
          :value="selected"
          :close="clearable"
          @click:close="handleInputRemove(item)"
        >
          {{ item[shortnameField] || item[nameField] }}
        </v-chip>
      </slot>
    </template>
  </v-autocomplete>
</template>

<script>
import BaseService from '@/services/services/tools/baseService';
import BaseAPI from '@/services/api/internal/tools/baseAPI';
import getConfig from '@/business/apiTreatment/request/simpleQueryParams';
import { mapGetters } from 'vuex';
import axios from 'axios';
import debounce from 'lodash/debounce';

const SEARCH_DEBOUNCE_DELAY = 1000;

export default {
  name: 'ServiceAutocomplete',
  props: {
    value: {
      required: true,
    },
    multiple: { type: Boolean, required: false, default: false },
    clearable: { type: Boolean, required: false, default: true },
    idField: { type: String, required: false, default: 'id' },
    nameField: { type: String, required: false, default: 'name' },
    shortnameField: { type: String, required: false, default: 'name' },
    outlined: { type: Boolean, required: false, default: true },
    dense: { type: Boolean, required: false, default: true },
    useEntities: {
      type: Boolean,
      required: false,
      default: true,
    },
    truncateEntities: {
      type: Number,
      required: false,
    },
    navFullIdField: {
      type: String,
      required: false,
      default: 'entity_nav_full_id',
    },
    queryParam: {
      type: Object,
      required: false,
      default: () => ({ limit: 50, offset: 0 }),
    },
    url: { type: String, required: false },
    variableUrl: { type: String, required: false },
    treatListResponse: {
      type: Function,
      required: false,
      default: (response) => response.data.items,
    },
    searchAttributesArray: {
      type: Array,
      required: false,
      default: () => ['name'],
    },
    baseQueryParam: {
      type: Object,
      required: false,
    },
    baseAndQueryParams: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data: () => ({
    items: [],
    isLoading: false,
    search: '',
    service: null,
    cancelToken: null,
  }),
  methods: {
    filter(item, queryText) {
      return this.searchAttributesArray.some(
        (attr) =>
          item[attr] &&
          item[attr]
            .toLocaleLowerCase()
            .indexOf(queryText.toLocaleLowerCase()) > -1
      );
    },
    handleInputRemove(item) {
      if (this.multiple) {
        const newValues = this.value.filter((el) => el !== item[this.idField]);
        this.$emit('input', newValues);
      } else {
        this.$emit('input', null);
      }
    },
    handleInputChange(value) {
      this.$emit('input', value);
      if (!this.multiple) {
        this.$emit(
          'newItem',
          this.items.find((el) => el[this.idField] === value)
        );
      }
      this.search = '';
    },
    getConfig,
    hasValues() {
      let values = [];
      if (this.multiple) {
        values = this.value;
      } else if (this.value) {
        values = [this.value];
      }
      return values.every((val) =>
        this.$refs.vuecomp.cachedItems.some(
          (item) => item[this.idField] === val
        )
      );
    },
    makeQuery() {
      if (this.url || this.variableUrl) {
        this.isLoading = true;
        if (this.cancelToken) {
          this.cancelToken.cancel();
        }
        this.cancelToken = axios.CancelToken.source();
        const idList = this.multiple ? this.value : this.value && [this.value];
        this.service
          .getItems(
            {
              params: this.getConfig({
                idList,
                idField: this.idField,
                searchField: this.search,
                noSearch: false,
                searchAttributesArray: this.searchAttributesArray,
                baseQueryParams: this.baseQueryParam,
                baseAndQueryParams: this.baseAndQueryParams,
                useEntities: this.useEntities,
                truncateEntities: this.truncateEntities,
                entities: this.entities,
                navFullIdField: this.navFullIdField,
              }),
              cancelToken: this.cancelToken.token,
            },
            this.variableUrl
          )
          .then((items) => {
            this.items = items;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    makeFirstQuery(idList = []) {
      if (this.url || this.variableUrl) {
        this.isLoading = true;

        this.service
          .getItems(
            {
              params: this.getConfig({
                idList,
                idField: this.idField,
                noSearch: true,
                searchAttributesArray: this.searchAttributesArray,
                baseQueryParams: this.baseQueryParam,
                baseAndQueryParams: this.baseAndQueryParams,
                useEntities: this.useEntities,
                truncateEntities: this.truncateEntities,
                entities: this.entities,
                navFullIdField: this.navFullIdField,
              }),
            },
            this.variableUrl
          )
          .then((items) => {
            this.items = items;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
  },
  computed: {
    ...mapGetters({ entities: 'entities/current' }),
  },
  created() {
    this.isLoading = true;
    this.service = new BaseService({
      api: new BaseAPI({ url: this.url }),
      treatListResponse: this.treatListResponse,
    });
    // eslint-disable-next-line func-names
    this.makeDebouncedQuery = debounce(this.makeQuery, SEARCH_DEBOUNCE_DELAY);
    if (
      (this.multiple && this.value.length) ||
      (!this.multiple && this.value)
    ) {
      this.makeFirstQuery(this.multiple ? this.value : [this.value]);
    }
  },
  watch: {
    search() {
      this.makeDebouncedQuery();
    },
    value() {
      this.makeQuery();
    },
    variableUrl() {
      this.makeQuery();
    },
    baseAndQueryParams() {
      this.makeQuery();
    },
    entities() {
      if (this.useEntities) {
        this.$refs.vuecomp.cachedItems = [];
        if (this.multiple) {
          if (this.value.length) {
            this.$emit('input', []);
          }
        } else if (this.value) {
          this.$emit('input', null);
        }
        this.$emit('clear-items');
        this.makeFirstQuery();
      }
    },
  },
};
</script>
